import React, { useState, useEffect } from 'react';
import { Typography, Grid, Card, CircularProgress, CardContent, Button, Box, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig'
import CustomModal from './../CustomModal';
import EditSurveyModal from './../EditSurveyModal';
import { colors } from '../../colors';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';

const DashboardView = () => {
    const [surveys, setSurveys] = useState([]);
    const [stats, setStats] = useState({
        totalSurveys: 0,
        totalResponses: 0,
        completionRate: 0
    });
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);  // For selected survey details
    const [dialogOpen, setDialogOpen] = useState(false);  // Control the modal
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [selectedSurveyId, setSelectedSurveyId] = useState(null);
    const [toggleAction, setToggleAction] = useState('');  // Track whether it's 'pause' or 'activate'
    const [editModalOpen, setEditModalOpen] = useState(false); // Edit modal state
    const [loading, setLoading] = useState(true);

    const handleDeleteClick = (surveyId) => {
        setSelectedSurveyId(surveyId);
        setModalTitle('Confirm Deletion');
        setModalMessage('Are you sure you want to delete this survey?');
        setConfirmationOpen(true);
    };

    const navigate = useNavigate();

    // Calculate total responses
    const calculateTotalResponses = (surveys) => {
        let total = 0;
        surveys.forEach(survey => total += survey.responsesCount || 0);
        return total;
    };

    // Calculate survey completion rate
    const calculateCompletionRate = (surveys) => {
        const totalCompleted = surveys.reduce((acc, survey) => acc + (survey.completed || 0), 0);
        const totalSurveys = surveys.length;
        return totalSurveys > 0 ? Math.round((totalCompleted / totalSurveys) * 100) : 0;
    };

    // Fetch surveys and statistics
    useEffect(() => {
        const fetchSurveys = async () => {
            try {
                const response = await axiosInstance.get('/api/surveys');
                setSurveys(response.data);
                setStats({
                    totalSurveys: response.data.length,
                    totalResponses: calculateTotalResponses(response.data),
                    completionRate: calculateCompletionRate(response.data)
                });
                setError(null);
                setSnackbarOpen(false);
            } catch (error) {
                setError('Error fetching surveys');
                setSnackbarOpen(true);
                console.error('Error fetching surveys:', error);
            }
            finally {
                setLoading(false);
            }
        };

        fetchSurveys();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress size={50} />
            </Box>
        );
    }

    // Navigate to the SurveyDashboard when the survey name is clicked
    const handleSurveyClick = (surveyId) => {
        navigate(`/admin/survey/${surveyId}`);
    };

    const handlePreviewClick = (surveyId) => {
        navigate(`/preview/${surveyId}`);
    };

    // Handle opening the edit modal
    const handleEditSurvey = (survey) => {
        // Reset the selected survey to avoid stale data
        setSelectedSurvey(null);
        setTimeout(() => {
            setSelectedSurvey({ ...survey }); // Create a copy of the selected survey to avoid direct mutation
            setEditModalOpen(true);
        }, 0); // Timeout ensures state is cleared before setting new survey
    };

    // Handle saving the edited survey
    const handleSaveEditedSurvey = async (updatedSurvey) => {
        try {
            await axiosInstance.put(`/api/surveys/${updatedSurvey.id}`, updatedSurvey);
            setSurveys(surveys.map(survey => (survey.id === updatedSurvey.id ? updatedSurvey : survey)));
            setSnackbarOpen(true);
            setError('Survey updated successfully!');
        } catch (error) {
            setError('Error updating survey');
            setSnackbarOpen(true);
        } finally {
            setEditModalOpen(false);
        }
    };

    // Handle delete survey
    const handleDeleteSurvey = async () => {
        setConfirmationOpen(false); // Close the confirmation modal
        try {
            await axiosInstance.delete(`/api/surveys/obliterate/${selectedSurveyId}`);

            // Remove deleted survey from the list
            setSurveys(surveys.filter(survey => survey.id !== selectedSurveyId));

            // Show success modal
            setModalTitle('Success');
            setModalMessage('Survey deleted successfully.');
        } catch (error) {
            // Show error modal
            setModalTitle('Error');
            setModalMessage('There was an issue deleting the survey.');
            console.error('Error deleting survey:', error);
        } finally {
            setModalOpen(true); // Open the result modal (success or error)
        }
    };

    // Open custom modal for pausing/activating survey
    const handleToggleActive = (surveyId, currentStatus) => {
        setSelectedSurveyId(surveyId);
        setToggleAction(currentStatus ? 'pause' : 'activate');
        setModalTitle(`Confirm ${currentStatus ? 'Pause' : 'Activate'} Survey`);
        setModalMessage(`Are you sure you want to ${currentStatus ? 'pause' : 'activate'} this survey?`);
        setConfirmationOpen(true);
    };

    // Handle pause/activate survey on confirmation
    const confirmToggleActive = async () => {
        setConfirmationOpen(false);
        try {
            await axiosInstance.put(`/api/surveys/${selectedSurveyId}/toggle-active`, { active: toggleAction === 'activate' });
            setSurveys(surveys.map(survey =>
                survey.id === selectedSurveyId ? { ...survey, active: toggleAction === 'activate' } : survey
            ));
            setModalTitle('Success');
            setModalMessage(`Survey ${toggleAction}d successfully.`);
        } catch (error) {
            setModalTitle('Error');
            setModalMessage('There was an issue updating the survey status.');
            console.error('Error updating survey status:', error);
        } finally {
            setModalOpen(true);
        }
    };

    // Handle view survey
    const handleViewSurvey = (survey) => {
        setSelectedSurvey(survey);
        setDialogOpen(true);
    };

    // Handle close modal
    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedSurvey(null);
    };

    // Handle Snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Handle template management click
    const handleTemplateManagementClick = () => {
        navigate('/admin/templates');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short', // 👈 shows timezone like IST
        });
    };

    return (
        <Box>
            <Box>
                {/* Survey Statistics */}
                <Grid container spacing={3} sx={{ mb: 5 }}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            borderLeft: `5px solid ${colors.racineRedDark}`,
                        }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ color: colors.racineRedDark, fontWeight: 'bold' }}>Total Surveys</Typography>
                                <Typography variant="h4" sx={{ color: '#000' }}>{stats.totalSurveys}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            borderLeft: `5px solid ${colors.racineRedDark}`,
                        }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ color: colors.racineRedDark, fontWeight: 'bold' }}>Total Responses</Typography>
                                <Typography variant="h4" sx={{ color: '#000' }}>{stats.totalResponses}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            borderLeft: `5px solid ${colors.racineRedDark}`,
                        }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ color: colors.racineRedDark, fontWeight: 'bold' }}>Completion Rate</Typography>
                                <Typography variant="h4" sx={{ color: '#000' }}>{stats.completionRate}%</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Recent Surveys */}
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                        mt: 4,
                    }}
                >
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            color: colors.racineRedDark,
                            fontWeight: 'bold',
                            textAlign: 'center',
                            mb: 3,
                        }}
                    >
                        Recent Surveys
                    </Typography>

                    {surveys.length > 0 ? (
                        <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                color: colors.primaryApplicationText,
                                                borderBottom: `2px solid ${colors.primaryApplicationText}`,
                                                width: '70%',
                                                padding: '12px',
                                            }}
                                        >
                                            Survey
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                color: colors.primaryApplicationText,
                                                borderBottom: `2px solid ${colors.primaryApplicationText}`,
                                                width: '5%',
                                                padding: '12px',
                                            }}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                color: colors.primaryApplicationText,
                                                borderBottom: `2px solid ${colors.primaryApplicationText}`,
                                                width: '25%',
                                                textAlign: 'center',
                                                padding: '12px',
                                            }}
                                            align="center"
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {surveys.map((survey) => (
                                        <TableRow
                                            key={survey.id}
                                            hover
                                            sx={{
                                                '&:hover': { backgroundColor: '#f9f9f9' },
                                            }}
                                        >
                                            {/* Survey Name and Description */}
                                            <TableCell sx={{ width: '40%', padding: '12px' }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            color: colors.primaryApplicationText,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                                color: colors.racineRedDark,
                                                            },
                                                        }}
                                                        onClick={() => handleSurveyClick(survey.id)}
                                                    >
                                                        {survey.surveyName}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        ({survey.templateSnapshot.title})
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        {survey.universityName}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        • {survey.cycle}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Status with Toggle */}
                                            <TableCell sx={{ width: '20%', padding: '12px' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: survey.active ? '#2e7d32' : '#d32f2f',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {survey.active ? 'Active' : 'Inactive'}
                                                </Typography>
                                                <Switch
                                                    checked={survey.active}
                                                    onChange={() => handleToggleActive(survey.id, survey.active)}
                                                    color="primary"
                                                    sx={{
                                                        '& .MuiSwitch-track': {
                                                            backgroundColor: survey.active ? '#2e7d32' : '#d32f2f',
                                                            opacity: 0.7,
                                                        },
                                                        '& .MuiSwitch-thumb': {
                                                            backgroundColor: survey.active ? '#ffffff' : '#ffebee',
                                                        },
                                                        '&:hover .MuiSwitch-track': {
                                                            opacity: 1,
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell sx={{ width: '40%', padding: '12px' }} align="center">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        sx={{
                                                            color: colors.primaryApplicationText,
                                                            borderColor: colors.primaryApplicationText,
                                                            textTransform: 'none',
                                                            '&:hover': { backgroundColor: '#FCEBEA' },
                                                        }}
                                                        onClick={() => handlePreviewClick(survey.id)}
                                                    >
                                                        Preview
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        sx={{
                                                            color: colors.primaryApplicationText,
                                                            borderColor: colors.primaryApplicationText,
                                                            textTransform: 'none',
                                                            '&:hover': { backgroundColor: '#FCEBEA' },
                                                        }}
                                                        onClick={() => handleViewSurvey(survey)}
                                                    >
                                                        View
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: colors.primaryApplicationText,
                                                            color: 'white',
                                                            textTransform: 'none',
                                                            '&:hover': { backgroundColor: '#B85D52' },
                                                        }}
                                                        onClick={() => handleEditSurvey(survey)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        sx={{
                                                            color: colors.racineRedDark,
                                                            borderColor: colors.racineRedDark,
                                                            textTransform: 'none',
                                                            '&:hover': { backgroundColor: '#B85D52' },
                                                        }}
                                                        onClick={() => handleDeleteClick(survey.id)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#aaa',
                                textAlign: 'center',
                                mt: 3,
                            }}
                        >
                            No surveys available.
                        </Typography>
                    )}
                    {/* Edit Survey Modal */}
                    {selectedSurvey && (
                        <EditSurveyModal
                            open={editModalOpen}
                            onClose={() => setEditModalOpen(false)}
                            onSave={handleSaveEditedSurvey}
                            survey={selectedSurvey}
                        />
                    )}
                    {/* Confirmation Modal for Delete or Pause/Activate */}
                    <CustomModal
                        open={confirmationOpen}
                        title={modalTitle}
                        message={modalMessage}
                        onClose={() => setConfirmationOpen(false)}
                        onConfirm={toggleAction === 'activate' || toggleAction === 'pause' ? confirmToggleActive : handleDeleteSurvey}
                        showConfirmButton={true}
                    />

                    {/* Result Modal */}
                    <CustomModal
                        open={modalOpen}
                        title={modalTitle}
                        message={modalMessage}
                        onClose={() => setModalOpen(false)}
                    />
                    {/* Snackbar for error messages */}
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                        message={error}
                    />
                    {/* Survey Details Modal */}
                    {/* Survey Details Modal */}
                    <Dialog
                        open={dialogOpen}
                        onClose={handleDialogClose}
                        maxWidth="md"
                        fullWidth
                        sx={{
                            '& .MuiPaper-root': {
                                borderRadius: '12px',
                                boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.15)',
                            },
                        }}
                    >
                        <DialogTitle
                            sx={{
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                color: colors.primaryApplicationText,
                                position: 'relative',
                                pb: 2,
                            }}
                        >
                            {selectedSurvey ? selectedSurvey.surveyName : 'Survey Details'}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 15,
                                    right: 20,
                                    textAlign: 'right',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.secondaryText,
                                        fontStyle: 'italic',
                                        mb: 1,
                                    }}
                                >
                                    Valid Till: {selectedSurvey ? new Date(selectedSurvey.validTill).toLocaleDateString() : ''}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.secondaryText,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Unique Links Count: {selectedSurvey ? selectedSurvey.uniqueLinksCount : ''}
                                </Typography>
                            </Box>
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                padding: '24px',
                                backgroundColor: '#fafafa',
                            }}
                        >
                            {selectedSurvey ? (
                                <Box
                                    sx={{
                                        p: 3,
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                        borderRadius: '10px',
                                        backgroundColor: '#ffffff',
                                    }}
                                >
                                    {/* Description Section */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: colors.primaryBannerBlue,
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Description
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 3,
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {selectedSurvey.surveyDescription}
                                    </Typography>

                                    {/* University Section */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: colors.primaryBannerBlue,
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        University
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 3,
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {selectedSurvey.universityName}
                                    </Typography>

                                    {/* Unique Links Section */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 4,
                                            mb: 2,
                                            color: colors.primaryBannerBlue,
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Unique Links
                                    </Typography>
                                    <List>
                                        {selectedSurvey.uniqueLinks.map((link, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    borderBottom: '1px solid #e0e0e0',
                                                    py: 1,
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: colors.primaryBannerBlue,
                                                        textDecoration: 'none',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {link}
                                                </a>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{ color: colors.secondaryText }}
                                    >
                                        Loading...
                                    </Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions
                            sx={{
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                p: 2,
                            }}
                        >
                            <Button
                                onClick={handleDialogClose}
                                variant="contained"
                                sx={{
                                    backgroundColor: colors.primaryBannerBlue,
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: colors.primaryApplicationText,
                                    },
                                }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardView;
